import type { Editor, EditorEvents } from "@tiptap/react";
import type { FC, ReactNode } from "react";

import { cn } from "../../utils/general";
import { EditorBubbleMenu, EditorContent, EditorProvider } from "../editor";
import { Separator } from "../separator";
import { EmailDraftAttachments } from "./email-draft-attachments";
import { useEmailDraft } from "./email-draft-context";
import { EmailDraftToolbar } from "./email-draft-toolbar";

export interface EmailDraftEditorProps {
  /** Read once, when the editor mounts. Later changes are ignored so the host can autosave freely. */
  initialContent: string;
  /** Renders in place of the editor, for a body that is still loading. */
  bodyPlaceholder?: ReactNode;
  /** Renders under the body, such as a quoted message. */
  afterBody?: ReactNode;
  /** While the after-body content is expanded, the body stops filling the space and sizes to its text. */
  afterBodyExpanded?: boolean;
}

export const EmailDraftEditor: FC<EmailDraftEditorProps> = ({
  initialContent,
  bodyPlaceholder,
  afterBody,
  afterBodyExpanded = false,
}) => {
  const {
    editorContainerRef,
    editorExtensions,
    handleUpdateContent,
    handleUploadAttachments,
    setRecipientsOpen,
    isEditable,
  } = useEmailDraft();

  if (bodyPlaceholder) {
    return <>{bodyPlaceholder}</>;
  }

  const handleUpdate = (update: EditorEvents["update"]) => {
    handleUpdateContent({
      html: update.editor.getHTML(),
      text: update.editor.getText(),
    });
  };

  const handleUpload = (_: Editor, files: File[]) => {
    handleUploadAttachments(files);
  };

  return (
    <EditorProvider
      key={isEditable ? "draft-editor-editable" : "draft-editor-not-editable"}
      content={initialContent}
      onUpdate={handleUpdate}
      onDropFile={handleUpload}
      onPasteFile={handleUpload}
      editable={isEditable}
      extensions={editorExtensions}
    >
      <div className="flex-1 flex flex-col min-h-0">
        <EditorBubbleMenu />

        <div
          ref={editorContainerRef}
          className="flex-1 flex flex-col gap-4 min-h-0 pb-2 px-2 -mx-2 overflow-y-auto mb-0 [@media(min-height:560px)]:min-h-72"
        >
          <EditorContent onFocus={() => setRecipientsOpen(false)} className={cn({ "flex-1": !afterBodyExpanded })} />

          {afterBody}

          <EmailDraftAttachments />
        </div>

        <div className="flex-0">
          <Separator />
          <EmailDraftToolbar />
        </div>
      </div>
    </EditorProvider>
  );
};
